// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.
import "../../../style/sassStyle";

import { DummyEvent, QuestionnaireInputProps } from "./QuestionnaireTypes";
import {
  MenuItem,
  Select,
  Theme,
  createStyles,
  makeStyles,
} from "@material-ui/core";
import React, { useCallback } from "react";

import QuestionnaireStandardFormControl from "./QuestionnaireStandardFormControl";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    select: {
      width: "max-content",
    },
  })
);

/**
 * Renders a select input in a questionaire.
 * You should not need to use this outside of the Questionnaire component.
 */
const QuestionnaireInputSelect = (props: QuestionnaireInputProps) => {
  const { question, onChange } = props;
  const classes = useStyles();

  if (question.type !== "select") {
    throw Error(
      `Invalid question type ${question.type} for QuestionnaireInputSelect.`
    );
  }

  const { native } = question;

  if (!question.selectOptions) {
    throw Error(
      `Question object passed to QuestionnaireInputSelect needs selectOptions`
    );
  }

  const onChangeWrapper = useCallback(
    (
      event: React.ChangeEvent<{ name?: string; value: unknown }>,
      child: React.ReactNode
    ) => {
      const value = event.target.value ? (event.target.value as string) : null;

      const dummyEvent: DummyEvent = {
        currentTarget: {
          name: question.name,
          value,
        },
      };
      onChange(dummyEvent);
    },
    [onChange, question.name]
  );

  const fieldId = question.name + "-field";

  const OptionComponent = native ? "option" : MenuItem;

  return (
    <QuestionnaireStandardFormControl
      error={question.error}
      label={question.label}
      fieldId={fieldId}
      mandatory={question.mandatory}
    >
      <Select
        value={question.value || ""}
        displayEmpty
        className={classes.select}
        autoComplete={question.autofill}
        inputProps={{
          "aria-label":
            typeof question.label === "string" ? question.label : "",
        }}
        onChange={onChangeWrapper}
        color="primary"
        variant="outlined"
        native={native}
      >
        <OptionComponent value="" disabled>
          {question.placeholder}
        </OptionComponent>
        {question.selectOptions.map(selectOption => (
          <OptionComponent key={selectOption.value} value={selectOption.value}>
            {selectOption.name}
          </OptionComponent>
        ))}
      </Select>
    </QuestionnaireStandardFormControl>
  );
};

export default QuestionnaireInputSelect;
